import * as React from 'react';
import { useUnreadCount } from '../hooks/useUnreadCount';
import { activitiesApi } from '../services/activitiesApi';
import { getUnreadCount, publishUnreadCount, resumeUnreadPolling } from '../services/unreadCountStore';
import type { ActivityItem as ActivityItemType, Audience } from '../types';
import { FeedSkeleton } from './ActivityFeed';
import { ActivityItem } from './ActivityItem';

export type ActivityBellProps = {
  dashboard_path: string;
  settings_path: string;
  audience?: Audience | null;
};

export function ActivityBell({ dashboard_path, settings_path, audience = null }: ActivityBellProps) {
  // Polling lives in a shared store so the mobile and desktop nav bells make
  // one request per interval, and a 401 there stops quietly.
  const unread = useUnreadCount();
  const [open, setOpen] = React.useState(false);
  const [items, setItems] = React.useState<ActivityItemType[]>([]);
  const [loading, setLoading] = React.useState(false);
  const rootRef = React.useRef<HTMLDivElement>(null);
  const toggleRef = React.useRef<HTMLButtonElement>(null);

  React.useEffect(() => {
    if (!open) return undefined;
    const onClick = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    // Escape closes the menu and hands focus back to the bell, so keyboard
    // users are never stuck inside it.
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
        toggleRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  // Tabbing past the last footer link closes the menu.
  const onBlur = (event: React.FocusEvent<HTMLDivElement>) => {
    const next = event.relatedTarget as Node | null;
    if (open && next && rootRef.current && !rootRef.current.contains(next)) {
      setOpen(false);
    }
  };

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (!next) return;
    setLoading(true);
    try {
      const data = await activitiesApi.list({ limit: 10 });
      setItems(data.activities);
      resumeUnreadPolling();
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  const openItem = async (item: ActivityItemType) => {
    try {
      await activitiesApi.markRead(item.id);
      if (!item.read_at) publishUnreadCount(Math.max(0, getUnreadCount() - 1));
    } catch {
      // Follow the link even if mark-read fails.
    }
    const url = item.action?.web_url;
    if (url) {
      window.location.assign(url);
    }
  };

  const badge = unread > 99 ? '99+' : String(unread);

  return (
    <div className="activity-bell dropdown" ref={rootRef} onBlur={onBlur}>
      <button
        ref={toggleRef}
        type="button"
        className="activity-bell__toggle btn btn-link position-relative d-inline-flex align-items-center justify-content-center"
        aria-label="Activity feed"
        aria-expanded={open}
        onClick={toggle}
      >
        <i className="fas fa-bell" aria-hidden="true" />
        {unread > 0 && (
          <span className="activity-bell__badge position-absolute badge rounded-pill bg-danger">
            {badge}
            <span className="visually-hidden">unread activities</span>
          </span>
        )}
      </button>
      {open && (
        <div className="activity-bell__menu dropdown-menu dropdown-menu-end show shadow">
          {/* Only the rows scroll, so the footer links stay in view. */}
          <div className="activity-bell__list">
            {loading && <FeedSkeleton rows={3} />}
            {!loading && items.length === 0 && (
              <div className="dropdown-item-text text-muted">No activity yet</div>
            )}
            {!loading &&
              items.map((item) => (
                <ActivityItem key={item.id} item={item} onOpen={openItem} audience={audience} />
              ))}
          </div>
          <div className="activity-bell__footer">
            <a className="dropdown-item activity-bell__link" href={dashboard_path}>
              See all
            </a>
            <a className="dropdown-item activity-bell__link" href={settings_path}>
              Notification preferences
            </a>
          </div>
        </div>
      )}
    </div>
  );
}
